"use client"

import {
  Box,
  Container,
  Heading,
  Text,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  SimpleGrid,
} from "@chakra-ui/react"

import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext"
import { usePageAccess } from "../../wrappers/PageAccessContext"

import { PeriodProvider } from "./components/revamp/PeriodContext"
import { neumorph } from "./components/revamp/neumorph"
import InsightsTopNav from "./components/revamp/InsightsTopNav"
import KpiHeader from "./components/revamp/KpiHeader"
import AlertPanel from "./components/revamp/AlertPanel"

// Trends section
import RevenueGpCard from "./components/revamp/cards/RevenueGpCard"
import GrowthCard from "./components/revamp/cards/GrowthCard"
import SalesVolumeCard from "./components/revamp/cards/SalesVolumeCard"
import DayOfWeekCard from "./components/revamp/cards/DayOfWeekCard"

// Performance section
import TopArticlesCard from "./components/revamp/cards/TopArticlesCard"
import BottomArticlesCard from "./components/revamp/cards/BottomArticlesCard"
import TopStoresCard from "./components/revamp/cards/TopStoresCard"
import BottomStoresCard from "./components/revamp/cards/BottomStoresCard"

// Inventory section
import DormantStockCard from "./components/revamp/cards/DormantStockCard"
import DcStockCard from "./components/revamp/cards/DcStockCard"
import ArticleStatusCard from "./components/revamp/cards/ArticleStatusCard"

const SectionHeading = ({ id, label, description }) => (
  <Box id={id} mt={6} mb={3} pt={4}>
    <Heading
      size="sm"
      color={neumorph.textPrimary}
      letterSpacing="0.05em"
      textTransform="uppercase"
      fontWeight="700"
    >
      {label}
    </Heading>
    {description && (
      <Text fontSize="xs" color={neumorph.textMuted} mt={1}>
        {description}
      </Text>
    )}
  </Box>
)

const Insights = () => {
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer()
  // Insights is now a grantable page like Sales or Margin — admins hand it out
  // per user on Users > Page Access. It used to be hard-gated to userid 1 and 2,
  // which meant nobody could be given it without a code change.
  const { restrictedyn, user_page_access, loading: accessLoading } = usePageAccess()
  const canView = user_page_access.some((page) => page.value === "Insights")

  if (accessLoading) {
    return (
      <Box minH="100vh" p={4} bg={neumorph.pageBgSolid}>
        <Text fontSize="sm" color={neumorph.textMuted}>
          Loading&hellip;
        </Text>
      </Box>
    )
  }

  if (!canView) {
    return (
      <Box minH="100vh" p={4} bg={neumorph.pageBgSolid}>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>You do not have access to the Insights page.</AlertDescription>
        </Alert>
      </Box>
    )
  }

  if (restrictedyn) {
    return (
      <Box minH="100vh" p={4} bg={neumorph.pageBgSolid}>
        <Alert status="warning" borderRadius="lg">
          <AlertIcon />
          <AlertTitle>Account Payment Required</AlertTitle>
        </Alert>
      </Box>
    )
  }

  if (!selectedSupplier || !selectedRetailer) {
    return (
      <Box minH="100vh" bg={neumorph.pageBg} p={4}>
        <PeriodProvider>
          <InsightsTopNav supplier={selectedSupplier} retailer={selectedRetailer} />
          <Container maxW="98vw" py={6}>
            <Box sx={{ ...neumorph.raised, p: 8, textAlign: "center" }}>
              <Text fontSize="md" color={neumorph.textMuted}>
                Select a supplier and retailer in the sidebar to load insights.
              </Text>
            </Box>
          </Container>
        </PeriodProvider>
      </Box>
    )
  }

  return (
    <Box minH="100vh" sx={{ background: neumorph.pageBg }}>
      <PeriodProvider>
        <InsightsTopNav supplier={selectedSupplier} retailer={selectedRetailer} />

        <Container maxW="98vw" pb={8}>
          {/* Overview section */}
          <Box id="overview" pt={4}>
            <KpiHeader supplier={selectedSupplier} retailer={selectedRetailer} />
            <AlertPanel supplier={selectedSupplier} retailer={selectedRetailer} />
          </Box>

          {/* Trends section */}
          <SectionHeading
            id="trends"
            label="Trends"
            description="Revenue, cost, growth — how the supplier is performing over time"
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
            <RevenueGpCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <GrowthCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <SalesVolumeCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <DayOfWeekCard supplier={selectedSupplier} retailer={selectedRetailer} />
          </SimpleGrid>

          {/* Performance section */}
          <SectionHeading
            id="performance"
            label="Performance"
            description="Top and bottom performers — articles and stores driving the numbers"
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={4}>
            <TopArticlesCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <BottomArticlesCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <TopStoresCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <BottomStoresCard supplier={selectedSupplier} retailer={selectedRetailer} />
          </SimpleGrid>

          {/* Inventory section */}
          <SectionHeading
            id="inventory"
            label="Inventory"
            description="Stock health — dormant capital, DC distribution, status flags"
          />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            <DormantStockCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <DcStockCard supplier={selectedSupplier} retailer={selectedRetailer} />
            <ArticleStatusCard supplier={selectedSupplier} retailer={selectedRetailer} />
          </SimpleGrid>

          {/* Bottom spacing */}
          <Box h="60px" />
        </Container>
      </PeriodProvider>
    </Box>
  )
}

export default Insights
